import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, NgZone, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertController, IonButton, IonButtons, IonHeader, IonIcon, IonTitle, IonToolbar, IonicSafeString, ModalController } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { enterLeave } from 'src/app/animations/common.animations';
import { ButtonComponent } from 'src/app/components/button/button.component';
import { TextInputControl } from 'src/app/form-controls/text-input/text-input.control';
import { TextAreaControl } from 'src/app/form-controls/textarea/textarea.control';
import { UnitQuantity, UnitQuantityControl } from 'src/app/form-controls/unit-quantity/unit-quantity.control';
import { ActionAlertTemplateLabelPipe } from 'src/app/pipes/action-alert-template-label.pipe';
import { ActionAlertTemplate, ItemTypeMetadata, SheetsRendererService } from 'src/app/services/sheets-renderer.service';
import { AppState } from 'src/app/store/reducers';
import { addSheetItemInstructionsRequested, removeSheetItemRequested, updateSheetItemInstructionsRequested } from 'src/app/store/sheet/sheet.actions';
import { ActionAlert, ItemProperties, Row } from 'src/app/types/aggregate-graph.types';

@Component({
    templateUrl: './sheet-item-editor.modal.html',
    styleUrls: ['./sheet-item-editor.modal.scss', '../sheet-item-editor/common.scss'],
    imports: [
        IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, NgComponentOutlet, ButtonComponent, TextAreaControl, ReactiveFormsModule,
        TextInputControl, UnitQuantityControl, ActionAlertTemplateLabelPipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [enterLeave]
})
export class SheetItemEditorModal implements OnInit {

	@Input() sheetId!: string;
	@Input() instructionTypeName!: string;
	@Input() item!: Row | null;
	@Input() minDate!: string;
	@Input() maxDate!: string;

	instructionsFormArray: FormArray;
	config!: ItemTypeMetadata;

	itemPropertiesFormGroup!: FormGroup<{
		name: FormControl<string | null>;
		notes: FormControl<string | null>;
	}>;

	alertsFormArray: FormArray<FormControl<UnitQuantity | number | null>> = new FormArray<FormControl<UnitQuantity | number | null>>([]);
	actionAlertTemplates!: ActionAlertTemplate[];

	constructor(
		private modalController: ModalController, private formBuilder: FormBuilder, private sheetsRendererService: SheetsRendererService,
		private store: Store<AppState>, private alertController: AlertController, private ngZone: NgZone) {
		this.instructionsFormArray = this.formBuilder.array<FormGroup>([], [Validators.required]);
	}

	ngOnInit(): void {
		this.itemPropertiesFormGroup = new FormGroup({
			name: new FormControl(this.item?.name?.trim() ?? null),
			notes: new FormControl(this.item?.notes?.trim() ?? null)
		});

		this.config = this.sheetsRendererService.getItemTypeMetadata(this.instructionTypeName);

		this.alertsFormArray = new FormArray<FormControl<UnitQuantity | number | null>>([]);

		this.actionAlertTemplates = this.sheetsRendererService.getActionAlertTemplates(this.instructionTypeName);

		this.actionAlertTemplates.forEach(template => {
			var alert = this.item?.actionAlerts.find(alert => alert.id === template.id);
			this.alertsFormArray.push(new FormControl(alert ? (alert.actionPropertyUnit === null ? alert.valueToCompareAgainst : { unit: alert.actionPropertyUnit, quantity: alert.valueToCompareAgainst }) : null));
		});
	}

	getItemEditorFormComponent() {
		return this.sheetsRendererService.getItemEditorFormType(this.instructionTypeName);
	}

	async saveInstructionsPayload() {
		if (this.instructionsFormArray.invalid) {
			const alert = await this.alertController.create({
				header: 'Input not valid',
				subHeader: 'Please check the following and retry:',
				message: new IonicSafeString('<div><ul><li>The end time cannot be before the start time.</li><li>All timings must be a multiple of 5 minutes.</li><li>All mandatory fields are completed.</li></ul></div>'),
				buttons: ['Ok'],
				backdropDismiss: false
			});
			alert.present();
			return;
		}

		const itemProperties: ItemProperties = { name: this.itemPropertiesFormGroup.value.name ?? null, notes: this.itemPropertiesFormGroup.value.notes ?? null };

		let actionAlerts: ActionAlert[] | null = null;

		if (this.alertsFormArray.dirty) {
			actionAlerts = [];
			this.alertsFormArray.value.forEach((value, index) => {
				if (value !== null && (typeof value === 'number' || value.quantity !== null)) { // must allow zero value to compare against
					const template = this.actionAlertTemplates[index];
					actionAlerts!.push({
						id: template.id,
						actionPropertyName: template.actionControlName,
						actionPropertyLabel: template.actionControlLabel,
						actionPropertyUnit: template.actionControlUnit,
						valueToCompareAgainst: typeof value === 'number' ? value : value.quantity!,
						alertCondition: template.alertCondition
					});
				}
			});
		}

		if (this.item) this.store.dispatch(updateSheetItemInstructionsRequested({ itemId: this.item.id, instructions: this.instructionsFormArray.value, itemProperties, actionAlerts }));
		else this.store.dispatch(addSheetItemInstructionsRequested({ instructions: this.instructionsFormArray.value, itemProperties, actionAlerts }));

		this.close();
	}

	async removeItem() {
		if (this.item!.isRemoved) return;

		const hasActions = this.item!.slots.some(slot => !!slot?.action);

		const alert = await this.alertController.create({
			subHeader: 'Remove item?',
			message: hasActions ? 'This will discontinue the item and remove future schedules' : 'This will delete the item from today\'s sheet',
			buttons: [
				'Cancel',
				{
					text: 'Remove',
					cssClass: 'button-critical',
					handler: () => {
						this.ngZone.run(() => this.store.dispatch(removeSheetItemRequested({ sheetId: this.sheetId, itemId: this.item!.id })));
						alert.dismiss(true);
						return false;
					}
				}
			],
			backdropDismiss: false
		});

		await alert.present();

		const { data } = await alert.onDidDismiss();

		if (data === true) this.close();
	}

	close() {
		this.modalController.dismiss();
	}
}
